<i18n locale="en" lang="json">
{
  "home": "Home",
  "werknemers": "Employees",
  "taken": "Tasks",
  "schadelastbeheersing": "Claims management",
  "correspondentie": "Correspondence",
  "afspraken": "Appointments",
  "rapporten": "Reports",
  "interventieZoeken": "Find intervention",
  "uwGegevens": "Organization details",
  "contracten": "Contracts",
  "facturen": "Invoices",
  "verwerkersovereenkomst": "Data processing agreement",
  "verzuimdossier": "Absenteeism",
  "instellingen": "Settings",
  "gebruikers": "User management",
  "zelfService": "Organizational data",
  "superAdmin": "Super admin",
  "requestAccess": "Request access",
  "releaseNews": "Release news",
  "klantenservice": "Customer support",
  "profiel": "Profile",
  "logUit": "Sign out",
  "Administrator": "Administrator",
  "Manager": "Manager"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "home": "Home",
  "werknemers": "Werknemers",
  "taken": "Taken",
  "schadelastbeheersing": "Schadelastbeheersing",
  "correspondentie": "Correspondentie",
  "afspraken": "Afspraken",
  "rapporten": "Rapporten",
  "interventieZoeken": "Interventie zoeken",
  "uwGegevens": "Organisatiegegevens",
  "contracten": "Contractinformatie",
  "facturen": "Facturen",
  "verwerkersovereenkomst": "Verwerkersovereenkomst",
  "verzuimdossier": "Verzuimdossier",
  "instellingen": "Instellingen",
  "gebruikers": "Gebruikersbeheer",
  "zelfService": "Organisatiegegevens",
  "superAdmin": "Super-admin",
  "requestAccess": "Toegang verzoeken",
  "releaseNews": "Releasenieuws",
  "klantenservice": "Klantenservice",
  "profiel": "Profiel",
  "logUit": "Log uit",
  "Administrator": "Administrator",
  "Manager": "Manager"
}
</i18n>

<template>
  <div>
    <base-app-bar
      :user-name="userName"
      :scopes="scopeOptions"
      :current-scope="currentScope"
      :profile-location="{ name: RouteName.MijnProfielMijnAccount }"
      logged-in
      :is-switching-scope="switchingScope"
      @update:current-scope="onChangeScope"
      @pressed-log-out="onPressLogOut"
      @pressed-menu="onPressMenu"
    />
    <base-side-menu v-model:is-open="isSideMenuOpen" :items="sideMenuItems" />
    <document-viewer-dialog
      v-if="pdfViewer.location"
      v-model:is-open="pdfViewer.open"
      :location="pdfViewer.location"
      document-type="pdf"
      :title="t(pdfViewer.title)"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  faAddressBook,
  faBuilding,
  faCalendarAlt,
  faChartLine,
  faCog,
  faCopy,
  faEuroSign,
  faFileCheck,
  faFileContract,
  faHome,
  faPhoneAlt,
  faSearch,
  faSignOut,
  faTasks,
  faToolbox,
  faUserCircle,
  faUserCrown,
  faUsers,
  faUserUnlock,
  faFiles,
} from '@fortawesome/pro-regular-svg-icons';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DocumentViewerDialog from '@/common/components/zvdzo/DocumentViewerDialog.vue';
import useSkeletonData from '@/common/composition/useSkeletonData';
import useLoginData from '@/common/composition/useLoginData';
import useSwitchScope from '@/common/composition/useSwitchScope';
import type { MenuItem } from '@/areas/skeleton/views/BaseSideMenu.vue';
import BaseSideMenu from '@/areas/skeleton/views/BaseSideMenu.vue';
import { RouteName } from '@/router/route-names';
import { router, isRouteAuthorised } from '@/plugins/router';
import { UserRole } from '@/common/composition/useRoles';
import BaseAppBar from './BaseAppBar.vue';

const { t } = useI18n();
const { currentScope, ownLoginId, scopes, userName } = useSkeletonData();
const { autorisatieData, getLogoutRoute, epdUrl } = useLoginData();

const { switchScope, loading: switchingScope } = useSwitchScope();

const isSideMenuOpen = ref(false);

const pdfViewer = reactive({ location: '', title: '', open: false });

const onPressLogOut = async () => {
  await router.push(getLogoutRoute());
};

const onPressMenu = () => {
  isSideMenuOpen.value = !isSideMenuOpen.value;
};

const sideMenuItems = computed<MenuItem[]>(() => {
  if (!autorisatieData.value) {
    return [];
  }

  const conditionalMenuItemGroups: MenuItem[][] = [
    [
      // Home
      {
        icon: faHome,
        label: t('home'),
        to: { name: RouteName.HomeIndex },
      },
    ],
    [
      // Werknemers
      {
        icon: faAddressBook,
        label: t('werknemers'),
        to: { name: RouteName.WerknemerIndex },
      },
      {
        icon: faTasks,
        label: t('taken'),
        to: { name: RouteName.ActiviteitenActiviteitenOverzicht },
      },
      {
        icon: faTasks,
        label: t('schadelastbeheersing'),
        to: { name: RouteName.SchadelastbeheersingSchadelastbeheersing },
      },
      {
        icon: faCopy,
        label: t('correspondentie'),
        to: {
          name: RouteName.ActiviteitenDocumenten,
        },
      },
      {
        icon: faCalendarAlt,
        label: t('afspraken'),
        to: autorisatieData.value.isZelfplannerAllowed
          ? { name: RouteName.ActiviteitenOverzicht }
          : { name: RouteName.ActiviteitenAfspraken },
      },
      autorisatieData.value.isRapportageAllowed
        ? {
            icon: faChartLine,
            label: t('rapporten'),
            to:
              autorisatieData.value.isATP ||
              autorisatieData.value.isAdministratiekantoor
                ? { name: RouteName.RapportenVerzuimhistorie }
                : { name: RouteName.RapportenBasis },
          }
        : {},
      autorisatieData.value.isOrganisatieEPDinPA
        ? {}
        : {
            icon: faSearch,
            label: t('interventieZoeken'),
            href: 'https://fasttrack.jointruetribe.com',
          },
    ],
    [
      // Organisatie
      {
        icon: faBuilding,
        label: t('uwGegevens'),
        to: { name: RouteName.UwGegevensIndex },
      },
      {
        icon: faFileContract,
        label: t('contracten'),
        to: {
          name: RouteName.ContractenIndex,
        },
      },
      {
        icon: faEuroSign,
        label: t('facturen'),
        to: {
          name: RouteName.FacturenIndex,
        },
      },
      {
        icon: faFileCheck,
        label: t('verwerkersovereenkomst'),
        to: { name: RouteName.VerwerkersovereenkomstIndex },
      },
      autorisatieData.value.isOrganisatieEPDinPA
        ? {
            icon: faFiles,
            label: t('verzuimdossier'),
            href: epdUrl.value,
          }
        : {},
    ],
    [
      // Instellingen
      {
        icon: faCog,
        label: t('instellingen'),
        to: {
          name: RouteName.SettingsAssurantie,
        },
      },
      {
        icon: faUsers,
        label: t('gebruikers'),
        to: {
          name: RouteName.GebruikersBeheerGebruikers,
        },
      },
      {
        icon: faToolbox,
        label: t('zelfService'),
        to: { name: RouteName.ZelfserviceIndex },
      },
    ],
    [
      // Beheer_ZvdZ
      {
        icon: faUserCrown,
        label: t('superAdmin'),
        to: { name: RouteName.AdminUserTools },
      },
      {
        icon: faUserUnlock,
        label: t('requestAccess'),
        to: {
          name: RouteName.BeheerZvdZRequestAccess,
        },
      },
    ],
    [
      // Klantenservice
      autorisatieData.value.isOrganisatieEPDinPA
        ? {}
        : {
            icon: faPhoneAlt,
            label: t('klantenservice'),
            to: {
              name: RouteName.HomeIndex,
              query: { tab: 'customerService' },
            },
          },
    ],
    [
      // Overig
      {
        icon: faUserCircle,
        label: t('profiel'),
        to: { name: RouteName.MijnProfielMijnAccount },
      },
      {
        icon: faSignOut,
        label: t('logUit'),
        onPressed: onPressLogOut,
        testId: 'log-out',
      },
    ],
  ];

  const filterRouteAuthorised = (item: MenuItem): boolean | undefined => {
    return (
      !item.to ||
      (autorisatieData.value &&
        isRouteAuthorised(item.to, autorisatieData.value))
    );
  };

  const visibleMenuItemGroups: MenuItem[][] = conditionalMenuItemGroups
    .map(group => group.filter(filterRouteAuthorised))
    .filter((group: MenuItem[]) => group.length > 0);

  const divider: MenuItem = { divider: true };
  const menuItems: MenuItem[] = [];
  visibleMenuItemGroups.forEach((group, index) => {
    if (index > 0) {
      menuItems.push(divider);
    }
    const menuItemGroup: MenuItem[] = group.map((item): MenuItem => item);
    menuItems.push(...menuItemGroup);
  }, []);
  return menuItems;
});

const scopeOptions = computed<{ value: string; title: string }[]>(() =>
  (scopes.value || []).map(scope => ({
    value: `${scope.loginId}:${scope.rolId}`,
    title: [
      t(`roleName.${UserRole[scope.rolId as UserRole] || 'unknown'}`),
      scope.loginId !== ownLoginId.value ? scope.gebruikersnaam : '',
      scope.organisatienaam,
    ]
      .filter(Boolean)
      .map(String)
      .join(' - '),
  })),
);

const onChangeScope = async (role: (string | undefined)[]) => {
  if (!role[0]) {
    return;
  }
  // Before we switch, we move to home to be sure the reload is done on a page the user can access
  await router.replace({ name: RouteName.HomeIndex });
  await switchScope(role[0]);

  // Reload the window to refresh all user data and be 100% sure all data and UI is correct
  window.location.reload();
};
</script>
